import { useState } from "react";
import { useParams } from "react-router-dom";

import DropdownMenuDivider from "../../../Menus/DropdownMenuDivider";
import DropdownOptionWithIcon, {
  MdiIconType,
} from "../../../Menus/DropdownOptionWithIcon";
import useCloudPermissions from "../../../../hooks/useCloudPermissions";
import useUpdateView from "../../../../api/api-hooks/views/useUpdateView";
import { View } from "../../../../types/PageInterfaces";
import { SharedViewModes } from "../../PageViewRenderer";
import AppDrawer from "../../../Drawers/AppDrawer";
import BasicTableView from "../../../../views/supportal/CloudListView/BasicTableView";
import IconButton from "../../../../elements/IconButton";
import { Alert } from "reactstrap";

const viewModes = [
  { id: "Table", Name: "Table View", icon: "mdi mdi-table" },
  { id: "Cards", Name: "Cards View", icon: "mdi mdi-card-multiple-outline" },
  { id: "Board", Name: "Kanban Board", icon: "mdi mdi-land-rows-vertical" },
  { id: "Timeline", Name: "Timeline", icon: "mdi mdi-timeline-check-outline" },
  { id: "Calendar", Name: "Calendar", icon: "mdi mdi-calendar" },
];

interface Props {
  setSelected: (selected: object) => void;
  view: View;
  parentViewID?: string;
  viewMode: SharedViewModes;
  setViewMode: (newMode: SharedViewModes) => void;
  modeButtonRef: React.MutableRefObject<any>;
}

/**
 * SharedModeButton - Dropdown button for swapping modes in a Table/Cards/Board view.
 */
const SharedModeButton = ({
  setSelected,
  view,
  parentViewID,
  viewMode,
  setViewMode,
  modeButtonRef,
}: Props) => {
  const { cPermissions } = useCloudPermissions();
  const { pageID }: any = useParams();
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const { updateView } = useUpdateView(pageID);
  const [error, setError] = useState<string | null>(null);

  if (!viewMode) return null;

  const onSaveAsDefault = (viewMode: any, field: "Type" | "MobileType") => {
    if (!view) return;

    // TODO: Show an error.
    if (view?.HiddenViews?.includes(viewMode)) {
      setError("You cannot set a hidden view as default.");

      setTimeout(() => {
        setError(null);
      }, 5000);

      return;
    }

    const payload = {
      ...view,
      [field]: viewMode,
    };

    //@ts-ignore
    updateView({
      viewID: view.id,
      payload: payload,
      parentViewID,
    });
  };

  const ViewOptionsComponent = (
    <>
      {viewModes.map((v) => {
        if (v.id === viewMode || view?.HiddenViews?.includes(v.id)) return null;

        return (
          <DropdownOptionWithIcon
            key={v.id}
            name={v.Name}
            onClick={() => updateViewMode(v.id as SharedViewModes)}
            icon={v.icon as MdiIconType}
            active={v.id === viewMode}
            isDefault={v.id === view.Type}
          />
        );
      })}
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        icon="mdi mdi-cog"
        name="View Mode Settings"
        disabled={!cPermissions.cloud_modify_field}
        onClick={() => {
          setManageDrawerOpen(true);
          modeButtonRef.current?.close?.();
        }}
      />
    </>
  );

  const Drawer = (
    <AppDrawer
      open={manageDrawerOpen}
      onClose={() => {
        setManageDrawerOpen(false);
        modeButtonRef.current?.open?.();
      }}
      headerTitle="View Modes"
      headerDescription="Set the default, show/hide view modes"
      body={
        <>
          <BasicTableView
            items={viewModes}
            loading={false}
            title=""
            getItemTitle={(i) => i.Name}
            getItemSubtitle={() => ""}
            CustomRenderer={(i) => {
              const isHidden = view?.HiddenViews?.includes(i.id);
              const isMobileDefault = view?.MobileType === i.id;

              return (
                <>
                  <IconButton
                    icon={isMobileDefault ? "cellphone-check" : "cellphone"}
                    onClick={() => onSaveAsDefault(i.id, "MobileType")}
                    color={isMobileDefault ? "success" : undefined}
                    tooltipText={"Set as default for mobile screen"}
                  />
                  <IconButton
                    icon={view?.Type === i.id ? "star" : "star-outline"}
                    onClick={() => onSaveAsDefault(i.id, "Type")}
                    color="warning"
                    tooltipText={"Set as default for desktop screen"}
                  />
                  <IconButton
                    icon={isHidden ? "eye-off" : "eye"}
                    onClick={() => handleHideViewModes(i.id as SharedViewModes)}
                    tooltipText={
                      isHidden ? "Unhide View Mode" : "Hide View Mode"
                    }
                  />
                </>
              );
            }}
          />
          {error && (
            <Alert color="danger" className="mt-2">
              {error}
            </Alert>
          )}
        </>
      }
    />
  );

  return { ViewOptionsComponent, Drawer };

  function handleHideViewModes(vm: SharedViewModes) {
    // Rejecting when is default view mode.
    if (vm === viewMode) {
      setError("You cannot hide the default view mode.");

      setTimeout(() => {
        setError(null);
      }, 5000);
      return;
    }
    let updatedHiddenViews = view?.HiddenViews ?? [];

    if (updatedHiddenViews.includes(vm))
      updatedHiddenViews = updatedHiddenViews.filter((pvm) => pvm !== vm);
    else updatedHiddenViews.push(vm);

    const payload = {
      ...view,
      HiddenViews: updatedHiddenViews,
    };

    //@ts-ignore
    updateView({
      viewID: view.id,
      payload: payload,
      parentViewID,
    });
  }

  function updateViewMode(newMode: SharedViewModes) {
    setViewMode(newMode);
    setSelected({});
  }
};

export default SharedModeButton;
